/**
 * Mapping between portal settings (localStorage cache) and the server-backed
 * decision settings stored on the user profile (`profile.settings`).
 *
 * The server document is the source of truth; localStorage is a cache so the
 * portal works offline and in local mode. Portal-internal field names predate
 * the server schema and are kept for compatibility:
 *   portal taxRate        <-> server settings.taxRatePct
 *   portal bidderPremium  <-> server settings.buyerPremiumPct
 *   portal sellerCosts    <-> server settings.sellerCostsPct
 *   portal fixedLotCost   <-> server settings.fixedLotCost
 *   portal targetMarginPct<-> server settings.targetMarginPct
 *   portal enableNotifications / notifyLeadMinutes <-> same names
 *
 * @module portal/modules/settingsSync
 */

import { sanitizePercentage, sanitizeCurrency } from './formatting';
import type { PortalSettings } from '../types';

/** Server-side decision settings shape (subset the portal manages). */
export interface ServerDecisionSettings {
  taxRatePct?: number;
  buyerPremiumPct?: number;
  sellerCostsPct?: number;
  fixedLotCost?: number;
  targetMarginPct?: number;
  budgetCap?: number | null;
  enableNotifications?: boolean;
  notifyLeadMinutes?: number[];
}

/** Build the server `settings` patch from current portal settings. */
export function toServerSettings(settings: PortalSettings): ServerDecisionSettings {
  const patch: ServerDecisionSettings = {
    taxRatePct: sanitizePercentage(settings.taxRate, 0),
    buyerPremiumPct: sanitizePercentage(settings.bidderPremium, 15),
    sellerCostsPct: sanitizePercentage(settings.sellerCosts, 14),
    fixedLotCost: sanitizeCurrency(settings.fixedLotCost, 0),
    targetMarginPct: sanitizePercentage(settings.targetMarginPct, 30),
  };
  if (typeof settings.budgetCap === 'number' && Number.isFinite(settings.budgetCap)) {
    patch.budgetCap = settings.budgetCap > 0 ? settings.budgetCap : null;
  }
  if (typeof settings.enableNotifications === 'boolean') {
    patch.enableNotifications = settings.enableNotifications;
  }
  if (Array.isArray(settings.notifyLeadMinutes)) {
    patch.notifyLeadMinutes = settings.notifyLeadMinutes.filter(
      (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0
    );
  }
  return patch;
}

/**
 * Apply server decision settings onto local portal settings.
 * Server values win for fields it defines. Returns true when anything changed.
 */
export function applyServerSettings(
  settings: PortalSettings,
  server: ServerDecisionSettings | null | undefined
): boolean {
  if (!server || typeof server !== 'object') return false;
  let changed = false;

  const applyNumber = (
    portalKey: 'taxRate' | 'bidderPremium' | 'sellerCosts' | 'fixedLotCost' | 'targetMarginPct',
    value: number | undefined
  ) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    if (settings[portalKey] !== value) {
      settings[portalKey] = value;
      changed = true;
    }
  };

  applyNumber('taxRate', server.taxRatePct);
  applyNumber('bidderPremium', server.buyerPremiumPct);
  applyNumber('sellerCosts', server.sellerCostsPct);
  applyNumber('fixedLotCost', server.fixedLotCost);
  applyNumber('targetMarginPct', server.targetMarginPct);

  if (server.budgetCap !== undefined) {
    const next =
      typeof server.budgetCap === 'number' && server.budgetCap > 0 ? server.budgetCap : null;
    if (settings.budgetCap !== next) {
      settings.budgetCap = next;
      changed = true;
    }
  }

  if (
    typeof server.enableNotifications === 'boolean' &&
    settings.enableNotifications !== server.enableNotifications
  ) {
    settings.enableNotifications = server.enableNotifications;
    changed = true;
  }

  if (Array.isArray(server.notifyLeadMinutes) && server.notifyLeadMinutes.length) {
    const current = Array.isArray(settings.notifyLeadMinutes) ? settings.notifyLeadMinutes : [];
    if (JSON.stringify(current) !== JSON.stringify(server.notifyLeadMinutes)) {
      settings.notifyLeadMinutes = [...server.notifyLeadMinutes];
      changed = true;
    }
  }

  return changed;
}
